import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { Subscription, Subject } from 'rxjs';
import { VerticalStepperService } from '../../../services/vertical-stepper/vertical-stepper.service';

@Component({
  selector: 'irembogov-service-viewer',
  templateUrl: './service-viewer.component.html',
  styleUrls: ['./service-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceViewerComponent
  extends FieldType
  implements OnInit, OnDestroy
{
  @ViewChild('hiddenSubmitButton', { static: true })
  hiddenSubmitButton?: ElementRef<HTMLButtonElement>;

  activeServiceIndex = 0;
  expandedServices: boolean[] = [];
  completedServices: boolean[] = [];
  private readonly subscriptions = new Subscription();
  private behaviorHandlerId?: string;

  isLoading = false;
  draftSavingTriggered = false;
  draftSavingInProgress = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    readonly cd: ChangeDetectorRef,
    private readonly verticalStepperService: VerticalStepperService
  ) {
    super();
  }

  ngOnInit(): void {
    // Initialize expanded state for all services
    if (this.visibleServiceFields.length > 0) {
      this.expandedServices = this.visibleServiceFields.map(
        (_, index) => index === 0
      );

      // Initialize completion state array
      this.completedServices = this.visibleServiceFields.map(() => false);
    }

    // Subscribe to draft saving events
    this.subscriptions.add(
      this.verticalStepperService.draftSavingModelObservable$.subscribe({
        next: () => {
          this.saveDraft();
        },
      })
    );

    // Register custom behavior for vertical steppers
    this.behaviorHandlerId =
      this.verticalStepperService.registerBehaviorOverride(
        `service-viewer-${this.field.key}`,
        (
          stepperIndex: number,
          isLastStep: boolean,
          field: FormlyFieldConfig
        ) => {
          // Only intercept if it's the last step in a stepper
          if (isLastStep) {
            // Wait for the child stepper to complete its operations
            setTimeout(() => {
              this.continueFromService(this.activeServiceIndex);
            }, 0);
            return true; // We handled this event
          }
          return false; // We didn't handle this event, proceed with default
        }
      );
  }

  /**
   * Get only visible service fields from the fieldGroup
   */
  get visibleServiceFields(): FormlyFieldConfig[] {
    return this.field.fieldGroup?.filter(f => !f.hide) ?? [];
  }

  /**
   * Check if a service has no sections to display (might be the case for optional services)
   */
  hasNoSections(serviceField: FormlyFieldConfig): boolean {
    return !serviceField.fieldGroup || serviceField.fieldGroup.length === 0;
  }

  /**
   * Check if all previous services are valid
   */
  isServiceValid(index: number): boolean {
    if (index === 0) return true;

    // Check if all previous services have valid form controls
    for (let i = 0; i < index; i++) {
      const serviceField = this.visibleServiceFields[i];

      // Skip validation for services that are hidden via hideExpression
      if (serviceField.hide) {
        continue;
      }

      // Check validity of the form control
      if (serviceField.formControl && !serviceField.formControl.valid) {
        return false;
      }
    }

    return true;
  }

  /**
   * Continue from the current service to the next or to review
   */
  continueFromService(index: number): void {
    const currentField = this.visibleServiceFields[index];

    // Check if the current service is valid
    // Skip validation for services that are hidden
    if (
      !currentField.hide &&
      currentField.formControl &&
      !currentField.formControl.valid
    ) {
      currentField.formControl.markAllAsTouched();
      return;
    }

    // Mark as completed
    this.completedServices[index] = true;

    // If this is the last service, proceed to summary
    if (index === this.visibleServiceFields.length - 1) {
      this.proceedToSummary();
      return;
    }

    // Otherwise, expand the next service
    const nextIndex = index + 1;
    this.expandedServices = this.expandedServices.map(
      (_, i) => i === nextIndex
    );
    this.activeServiceIndex = nextIndex;

    this.cd.markForCheck();
  }

  /**
   * Expand a service section
   */
  expandService(index: number): void {
    if (!this.isServiceValid(index)) {
      // Don't allow expanding if previous services are invalid
      return;
    }

    this.expandedServices = this.expandedServices.map((_, i) => i === index);
    this.activeServiceIndex = index;

    this.cd.markForCheck();
  }

  /**
   * Edit a service section that has been previously completed
   */
  editService(index: number, event: Event): void {
    // Stop event propagation to prevent the expandService method from being called
    event.stopPropagation();

    // Expand this service section
    this.expandedServices = this.expandedServices.map((_, i) => i === index);
    this.activeServiceIndex = index;

    this.cd.markForCheck();
  }

  /**
   * Save the form draft
   */
  saveDraft(): void {
    this.draftSavingTriggered = true;
    this.draftSavingInProgress = true;
    this.cd.markForCheck();

    // Trigger draft saving in parent component
    this.verticalStepperService.triggerDraftSavingBoolean(true);

    // Simulate completion of saving
    setTimeout(() => {
      this.draftSavingInProgress = false;
      this.cd.markForCheck();

      // Hide the saved message after 3 seconds
      setTimeout(() => {
        this.draftSavingTriggered = false;
        this.cd.markForCheck();
      }, 3000);
    }, 1000);
  }

  /**
   * Proceed to summary view
   */
  proceedToSummary(): void {
    // Simulate submit button click to trigger form submission
    if (this.hiddenSubmitButton) {
      this.hiddenSubmitButton.nativeElement.click();
    }
  }

  ngOnDestroy(): void {
    // Unregister custom behavior
    if (this.behaviorHandlerId) {
      this.verticalStepperService.unregisterBehaviorOverride(
        this.behaviorHandlerId
      );
    }

    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
